import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { fmtFeedValue, humanizeColumn } from '../../utils/pitchbookDiff';
import { Database, ExternalLink, GraduationCap, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_LABELS = {
    company: 'Company profile',
    investor: 'Investor profile',
    person: 'Person profile',
};

// Ordered (section, pattern) rules over raw PitchBook column names. First
// match wins and array order = display order; anything unmatched lands in
// "Other". Mirrors the curated sections of the Data Insights tab so both
// tabs read the same way.
const SECTION_RULES = [
    ['Role & Employer',        /^Primary(Company|Position)/i],
    ['Primary Contact',        /^PrimaryContact/i],
    ['Status & Ownership',     /Status|Universe|InvestorType|Ownership|Financing|Fundraising/i],
    ['Funds & Capital',        /Fund|AUM|DryPowder|Raised|Valuation|RoundAmount/i],
    ['Financials',             /Revenue|Profit|Income|EBIT|Debt|EnterpriseValue|Fiscal|PeriodEnd/i],
    ['Investment Activity',    /^Total|Portfolio|Exits|InvestmentProfessional/i],
    ['Industry & Focus',       /Industr|Vertical|EmergingSpaces|Keywords|TradeAssociations/i],
    ['Corporate Structure',    /Parent|Exchange|Ticker|AlternateOffice/i],
    ['Location & Contact Info', /^HQ|^Location$|^(Phone|MobilePhone|Fax|Email)$/i],
    ['Web & Social',           /URL|Website|ProfileLink|ProfileDataSource/i],
    ['Identity & Background',  /Name|Description|YearFounded|Employees|Prefix|Gender|Biography|University|YearOfBirth/i],
];
const OTHER_SECTION = 'Other';

function sectionFor(column) {
    for (const [section, pattern] of SECTION_RULES) {
        if (pattern.test(column)) return section;
    }
    return OTHER_SECTION;
}

function groupBySection(fields) {
    const buckets = new Map();
    for (const f of fields) {
        const s = sectionFor(f.column);
        if (!buckets.has(s)) buckets.set(s, []);
        buckets.get(s).push(f);
    }
    const order = [...SECTION_RULES.map(([s]) => s), OTHER_SECTION];
    return order.filter(s => buckets.has(s)).map(s => [s, buckets.get(s)]);
}

function FieldCell({ row }) {
    const display = fmtFeedValue(row.value);
    if (display === null) return null;
    const isUrl = typeof row.value === 'string' && /^https?:\/\//i.test(row.value);
    return (
        <div className="flex gap-3 min-w-0">
            <span className="w-40 text-xs font-semibold tracking-wider uppercase text-stone-400 shrink-0">
                {humanizeColumn(row.column)}
                {row.imported && row.crm_field && (
                    <span className="block normal-case tracking-normal font-normal text-[10px] text-emerald-600">→ {row.crm_field}</span>
                )}
            </span>
            {isUrl ? (
                <a href={row.value} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-blue-600 break-all hover:underline inline-flex items-start gap-1 min-w-0">
                    <span className="truncate">{display}</span>
                    <ExternalLink className="w-3 h-3 shrink-0 mt-1" />
                </a>
            ) : (
                <span className="text-sm break-words text-stone-700 min-w-0">{display}</span>
            )}
        </div>
    );
}

function FieldSection({ title, fields }) {
    const visible = fields.filter(f => fmtFeedValue(f.value) !== null);
    if (!visible.length) return null;
    return (
        <section className="overflow-hidden bg-white rounded-lg border border-stone-200">
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50/60">
                <h4 className="text-xs font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h4>
            </div>
            <div className="grid grid-cols-1 gap-y-3 gap-x-8 p-4 md:grid-cols-2">
                {visible.map(f => <FieldCell key={f.column} row={f} />)}
            </div>
        </section>
    );
}

function RecordCard({ record }) {
    const imported = (record.fields || []).filter(f => f.imported);
    const additional = (record.fields || []).filter(f => !f.imported);
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {ENTITY_LABELS[record.entity] || record.entity}
                </h3>
                {record.last_updated && (
                    <span className="text-[10px] text-stone-400">PitchBook last updated {record.last_updated}</span>
                )}
            </div>

            {additional.length > 0 && (
                <div className="space-y-3 mb-4">
                    {groupBySection(additional).map(([section, fields]) => (
                        <FieldSection key={section} title={section} fields={fields} />
                    ))}
                </div>
            )}

            {Array.isArray(record.education) && record.education.length > 0 && (
                <div className="mb-4 overflow-hidden bg-white rounded-lg border border-stone-200">
                    <div className="px-4 py-2 border-b border-stone-100 bg-stone-50/60">
                        <h4 className="text-xs font-semibold text-stone-700 flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <GraduationCap className="w-3.5 h-3.5" /> Education
                        </h4>
                    </div>
                    <div className="px-4 py-2">
                        {record.education.map((e, i) => (
                            <div key={i} className="py-1.5 border-b border-stone-100 last:border-0">
                                <div className="text-sm text-stone-900">{e.institute}</div>
                                <div className="text-xs text-stone-500">
                                    {[e.degree, e.major, e.graduating_year].filter(Boolean).join(' · ')}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {imported.length > 0 && (
                <details className="group">
                    <summary className="text-xs font-medium text-stone-500 cursor-pointer select-none hover:text-stone-700">
                        Fields imported to the CRM record ({imported.length})
                    </summary>
                    <div className="mt-2 space-y-3">
                        {groupBySection(imported).map(([section, fields]) => (
                            <FieldSection key={section} title={section} fields={fields} />
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

/**
 * Read-only PitchBook data tab for Company / Contact detail pages.
 * Shows everything the feed knows about the linked record, split into
 * "additional data" (columns not mapped to a CRM field) and a collapsible
 * list of columns that were imported into normal CRM fields.
 */
export default function PitchBookRecordTab({ crmType, crmId }) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState([]);

    useEffect(() => {
        if (!crmId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/pitchbook/record/${crmType}/${crmId}`,
                    { headers: getAuthHeader() });
                if (!cancelled) setRecords(res.data?.records || []);
            } catch {
                if (!cancelled) setRecords([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crmType, crmId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40 text-stone-400">
                <Loader2 className="w-5 h-5 animate-spin" />
            </div>
        );
    }

    if (!records.length) {
        return (
            <div className="flex flex-col items-center justify-center h-40 bg-stone-50 rounded-xl border border-stone-200 text-stone-400 gap-2">
                <Database className="w-6 h-6" />
                <p className="text-sm">No PitchBook data is linked to this record.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {records.map(r => <RecordCard key={`${r.entity}_${r.pitchbook_id}`} record={r} />)}
        </div>
    );
}
